import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronLeft, ChevronRight, BookOpen, Menu, X } from 'lucide-react';

function MarkdownComponents(darkMode) {
  const codeBg = darkMode ? 'background:#1e1e2e' : 'background:#1e1e2e';
  const inlineBg = darkMode ? '#2d2d3f' : '#f0f4ff';
  const inlineColor = darkMode ? '#93c5fd' : '#2563eb';
  const borderCol = darkMode ? '#374151' : '#e5e7eb';
  const headingBorder = darkMode ? '#1d4ed8' : '#3b82f6';
  const subtextCol = darkMode ? '#9ca3af' : '#6b7280';

  return {
    h1: ({ children }) => (
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: '0 0 1rem', paddingBottom: '0.5rem', borderBottom: `2px solid ${headingBorder}` }}>
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#3b82f6', margin: '1.75rem 0 0.75rem' }}>
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: '1.25rem 0 0.5rem' }}>
        {children}
      </h3>
    ),
    p: ({ children }) => (
      <p style={{ marginBottom: '1rem', lineHeight: 1.75 }}>{children}</p>
    ),
    code: ({ node, inline, className, children, ...props }) => {
      if (inline) {
        return (
          <code style={{ background: inlineBg, color: inlineColor, padding: '0.15em 0.4em', borderRadius: '0.3em', fontSize: '0.875em', fontFamily: 'monospace' }}>
            {children}
          </code>
        );
      }
      return (
        <code style={{ fontFamily: 'monospace', fontSize: '0.85em' }} {...props}>
          {children}
        </code>
      );
    },
    pre: ({ children }) => (
      <pre style={{ background: '#0d1117', color: '#e6edf3', padding: '1.25rem', borderRadius: '0.75rem', overflowX: 'auto', marginBottom: '1.25rem', fontSize: '0.85em', lineHeight: 1.6, fontFamily: 'monospace' }}>
        {children}
      </pre>
    ),
    ul: ({ children }) => (
      <ul style={{ listStyleType: 'disc', paddingLeft: '1.5rem', marginBottom: '1rem' }}>{children}</ul>
    ),
    ol: ({ children }) => (
      <ol style={{ listStyleType: 'decimal', paddingLeft: '1.5rem', marginBottom: '1rem' }}>{children}</ol>
    ),
    li: ({ children }) => (
      <li style={{ marginBottom: '0.35rem', lineHeight: 1.7 }}>{children}</li>
    ),
    table: ({ children }) => (
      <div style={{ overflowX: 'auto', marginBottom: '1.25rem' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>{children}</table>
      </div>
    ),
    thead: ({ children }) => (
      <thead style={{ background: darkMode ? '#1f2937' : '#f3f4f6' }}>{children}</thead>
    ),
    th: ({ children }) => (
      <th style={{ padding: '0.6rem 1rem', textAlign: 'left', fontWeight: 600, border: `1px solid ${borderCol}` }}>
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td style={{ padding: '0.6rem 1rem', border: `1px solid ${borderCol}` }}>{children}</td>
    ),
    blockquote: ({ children }) => (
      <blockquote style={{ borderLeft: '4px solid #3b82f6', paddingLeft: '1rem', color: subtextCol, margin: '1rem 0', fontStyle: 'italic' }}>
        {children}
      </blockquote>
    ),
    strong: ({ children }) => <strong style={{ fontWeight: 700 }}>{children}</strong>,
    a: ({ href, children }) => (
      <a href={href} style={{ color: '#3b82f6', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ),
    hr: () => <hr style={{ border: 'none', borderTop: `1px solid ${borderCol}`, margin: '1.5rem 0' }} />,
  };
}

export default function AcademyLayout({ darkMode, lessons, courseTitle, courseIcon, backPath = '/academy' }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const bg = darkMode ? '#111827' : '#f9fafb';
  const cardBg = darkMode ? '#1f2937' : '#ffffff';
  const sidebarBg = darkMode ? '#111827' : '#f3f4f6';
  const textColor = darkMode ? '#f9fafb' : '#111827';
  const subtextColor = darkMode ? '#9ca3af' : '#6b7280';
  const borderColor = darkMode ? '#374151' : '#e5e7eb';
  const topBarBg = darkMode ? '#1f2937' : '#ffffff';

  const currentLesson = lessons[activeIndex];
  const hasPrev = activeIndex > 0;
  const hasNext = activeIndex < lessons.length - 1;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeIndex]);

  const handleLessonClick = (index) => {
    setActiveIndex(index);
    setSidebarOpen(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: bg, color: textColor }}>

      {/* Top breadcrumb bar */}
      <div style={{ background: topBarBg, borderBottom: `1px solid ${borderColor}`, padding: '0.75rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: '64px', zIndex: 40 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={{ display: 'none', padding: '0.35rem', borderRadius: '0.5rem', background: 'transparent', border: 'none', cursor: 'pointer', color: textColor }}
            className="academy-mobile-menu-btn"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <Link to={backPath} style={{ color: subtextColor, textDecoration: 'none', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.2rem' }}
            onMouseEnter={e => e.currentTarget.style.color = '#3b82f6'}
            onMouseLeave={e => e.currentTarget.style.color = subtextColor}
          >
            <ChevronLeft size={15} />
            Academy
          </Link>
          <span style={{ color: subtextColor, fontSize: '0.875rem' }}>/</span>
          <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{courseTitle}</span>
          <span style={{ color: subtextColor, fontSize: '0.875rem' }}>/</span>
          <span style={{ color: subtextColor, fontSize: '0.875rem' }}>{currentLesson.title}</span>
        </div>
        <span style={{ fontSize: '1.5rem' }}>{courseIcon}</span>
      </div>

      <div style={{ display: 'flex', position: 'relative' }}>

        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            onClick={() => setSidebarOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 30 }}
            className="academy-overlay"
          />
        )}

        {/* Sidebar */}
        <aside
          className={`academy-sidebar ${sidebarOpen ? 'academy-sidebar-open' : ''}`}
          style={{ width: '240px', flexShrink: 0, background: sidebarBg, borderRight: `1px solid ${borderColor}`, padding: '1.25rem 0.75rem', minHeight: 'calc(100vh - 120px)', position: 'sticky', top: '120px', alignSelf: 'flex-start' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingLeft: '0.5rem' }}>
            <BookOpen size={16} style={{ color: '#3b82f6' }} />
            <span style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#3b82f6' }}>
              Lektionen
            </span>
          </div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {lessons.map((lesson, index) => {
              const isActive = activeIndex === index;
              return (
                <li key={lesson.slug}>
                  <button
                    onClick={() => handleLessonClick(index)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '0.6rem 0.75rem',
                      borderRadius: '0.5rem',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: isActive ? 600 : 400,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.6rem',
                      marginBottom: '0.2rem',
                      transition: 'all 0.15s',
                      background: isActive
                        ? darkMode ? 'rgba(37,99,235,0.25)' : '#eff6ff'
                        : 'transparent',
                      color: isActive
                        ? darkMode ? '#93c5fd' : '#1d4ed8'
                        : textColor,
                    }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = darkMode ? '#374151' : '#e5e7eb'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ fontSize: '1rem' }}>{lesson.icon}</span>
                    <span style={{ flex: 1 }}>{lesson.title}</span>
                    {isActive && <ChevronRight size={14} />}
                  </button>
                </li>
              );
            })}
          </ul>

          <div style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: `1px solid ${borderColor}` }}>
            <Link
              to={backPath}
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: subtextColor, textDecoration: 'none', fontSize: '0.85rem', padding: '0 0.5rem' }}
              onMouseEnter={e => e.currentTarget.style.color = '#3b82f6'}
              onMouseLeave={e => e.currentTarget.style.color = subtextColor}
            >
              <ChevronLeft size={14} />
              Zurück zur Academy
            </Link>
          </div>
        </aside>

        {/* Main content */}
        <main style={{ flex: 1, minWidth: 0, padding: '2rem 1.5rem', maxWidth: '860px' }}>

          {/* Lesson card */}
          <article style={{ background: cardBg, borderRadius: '1rem', padding: '2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              <span style={{ fontSize: '2.5rem' }}>{currentLesson.icon}</span>
              <div>
                <p style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: subtextColor, marginBottom: '0.25rem' }}>
                  {courseTitle}
                </p>
                <h1 style={{ fontSize: '1.75rem', fontWeight: 700, margin: 0 }}>{currentLesson.title}</h1>
              </div>
            </div>

            <div style={{ color: textColor }}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={MarkdownComponents(darkMode)}
              >
                {currentLesson.content}
              </ReactMarkdown>
            </div>
          </article>

          {/* Progress indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            {lessons.map((lesson, index) => (
              <button
                key={lesson.slug}
                onClick={() => setActiveIndex(index)}
                title={lesson.title}
                style={{
                  width: index === activeIndex ? '2rem' : '0.5rem',
                  height: '0.5rem',
                  borderRadius: '1rem',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  background: index === activeIndex ? '#3b82f6' : index < activeIndex ? '#93c5fd' : borderColor,
                }}
              />
            ))}
            <span style={{ fontSize: '0.8rem', color: subtextColor, marginLeft: '0.5rem' }}>
              {activeIndex + 1} / {lessons.length}
            </span>
          </div>

          {/* Prev / Next */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between' }}>
            {hasPrev ? (
              <button
                onClick={() => setActiveIndex(activeIndex - 1)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.25rem', borderRadius: '0.75rem', background: cardBg, border: `1px solid ${borderColor}`, color: textColor, cursor: 'pointer', fontSize: '0.9rem', fontWeight: 500, transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.color = '#3b82f6'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = borderColor; e.currentTarget.style.color = textColor; }}
              >
                <ChevronLeft size={16} />
                {lessons[activeIndex - 1].title}
              </button>
            ) : <div />}

            {hasNext && (
              <button
                onClick={() => setActiveIndex(activeIndex + 1)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.25rem', borderRadius: '0.75rem', background: '#3b82f6', border: 'none', color: '#ffffff', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 500, transition: 'background 0.2s', marginLeft: 'auto' }}
                onMouseEnter={e => e.currentTarget.style.background = '#2563eb'}
                onMouseLeave={e => e.currentTarget.style.background = '#3b82f6'}
              >
                {lessons[activeIndex + 1].title}
                <ChevronRight size={16} />
              </button>
            )}
          </div>
        </main>
      </div>

      <style>{`
        @media (max-width: 1023px) {
          .academy-mobile-menu-btn { display: flex !important; }
          .academy-sidebar {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            height: 100vh !important;
            z-index: 40 !important;
            transform: translateX(-100%);
            transition: transform 0.3s ease;
          }
          .academy-sidebar-open {
            transform: translateX(0) !important;
          }
        }
      `}</style>
    </div>
  );
}
